import { LoanSearchParams } from '@hyphengroup/cms-components/utils/searchParams/loanSearchParams';
import { useSafeSearchParams } from '@hyphengroup/cms-components/utils/useSafeSearchParams';
import { usePathname } from 'next/navigation';
import { cmsQueryParamsPrefix } from './types';

export const useLoanSearchParams = (
  initialParams: LoanSearchParams,
): {
  loanSearchParams: LoanSearchParams;
  /**
   * Only what the URL validly carries — no defaults filled in. For a consumer
   * that must tell "the reader said 10000" from "nobody said anything".
   */
  fromUrl: Partial<LoanSearchParams>;
  setLoanSearchParams: (params: Partial<LoanSearchParams>) => LoanSearchParams;
} => {
  const pathname = usePathname();
  const searchParams = useSafeSearchParams();
  // A loan is a positive whole number of currency units over a positive whole
  // number of months. `!Number.isNaN(+raw)` alone let `" "`, `"0"` and
  // `"-5000"` through — `" "` is truthy, so it beat the brand default and
  // arrived as `Number(" ") === 0`, driving both the widget's displayed value
  // and a real calculation call. Now that one address feeds the whole page's
  // plan, that reaches every block at once.
  const valid = (raw: string | null | undefined) => {
    if (!raw?.trim()) return undefined;
    const n = Number(raw);
    return Number.isInteger(n) && n > 0 ? raw : undefined;
  };
  const fromUrl: Partial<LoanSearchParams> = {
    amount: valid(
      searchParams?.get('amount') ||
        searchParams?.get(`${cmsQueryParamsPrefix}amount`),
    ),
    tenure: valid(
      searchParams?.get('tenure') ||
        searchParams?.get(`${cmsQueryParamsPrefix}tenure`),
    ),
  };
  const loanSearchParams: LoanSearchParams = {
    amount: fromUrl.amount ?? initialParams?.amount,
    tenure: fromUrl.tenure ?? initialParams?.tenure,
  };

  const setLoanSearchParams = (
    params: Partial<LoanSearchParams>,
  ): LoanSearchParams => {
    const obj = loanSearchParams;
    const paramsFromLocation = new URLSearchParams(window.location.search);
    if (params.amount) {
      paramsFromLocation.set(`${cmsQueryParamsPrefix}amount`, params.amount);
      paramsFromLocation.delete(`amount`);
      obj.amount = params.amount;
    }
    if (params.tenure) {
      paramsFromLocation.set(`${cmsQueryParamsPrefix}tenure`, params.tenure);
      paramsFromLocation.delete(`tenure`);
      obj.tenure = params.tenure;
    }
    const url = `${pathname}?${paramsFromLocation.toString()}`;
    window.history.pushState(null, '', url);
    return obj;
  };

  return { loanSearchParams, fromUrl, setLoanSearchParams };
};
